import React from 'react';

import { generatePageTitle } from '@elara/client-core/lib/page-title';

import { useRouteParams, useRouterState, useSharedItineraryState } from '#states/hooks.js';
import { Button, Loader, Logo } from '#ui';
import { PageNotFound } from '#views/error/PageNotFound.js';

import { SharedItineraryView } from './SharedItineraryView.js';

/**
 * Keeps search engines off a shared itinerary while the page is mounted. Hosting and the API also
 * send `X-Robots-Tag: noindex`; this covers crawlers that only read the rendered document.
 */
function useNoIndex() {
  React.useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.append(meta);
    return () => meta.remove();
  }, []);
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-dvh w-full flex flex-col items-center justify-center gap-6 p-6 text-center">{children}</div>
  );
}

/**
 * Route component for `/share/:token` — the public, read-only itinerary a traveler shares with
 * friends and family. Rendered standalone (no sign-in or membership gates); the visitor needs no
 * account. Reloads quietly when the tab returns after a while, so an open page stays current. The
 * tab is titled after the trip; GA4 is pinned to the generic route title on share pages
 * (`firebase-platform.web.ts`), so the trip name never reaches it as `page_title`.
 */
export function SharedItineraryPage() {
  const { token } = useRouteParams();
  const sharedItineraryState = useSharedItineraryState();
  const routerState = useRouterState();
  const { status } = sharedItineraryState;
  const tripTitle = status.kind === 'ready' ? status.itinerary.trip.title : undefined;

  useNoIndex();

  React.useEffect(() => {
    if (token) void sharedItineraryState.load(token);
  }, [token, sharedItineraryState]);

  // Name the tab (and a printout's header) after the trip once it loads.
  React.useEffect(() => {
    if (tripTitle) routerState.setPageTitle(generatePageTitle(tripTitle));
  }, [tripTitle, routerState]);

  React.useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') void sharedItineraryState.refreshIfStale();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [sharedItineraryState]);

  switch (status.kind) {
    case 'ready':
      return <SharedItineraryView itinerary={status.itinerary} />;

    case 'not_found':
      // A revoked, unknown or archived link looks like any other missing page.
      return <PageNotFound />;

    case 'error':
      return (
        <Centered>
          <Logo variant="symbol" width={40} height={40} />
          <div className="flex flex-col gap-2 max-w-prose">
            <h1 className="text-2xl font-display font-light">We couldn’t load this itinerary</h1>
            <p className="text-muted-foreground">Please check your connection and try again.</p>
          </div>
          <Button variant="outline" onClick={() => token && void sharedItineraryState.load(token)}>
            Try again
          </Button>
        </Centered>
      );

    default:
      return (
        <Centered>
          <Loader loading={true} />
        </Centered>
      );
  }
}
